import React, { useState } from "react";
import axios from "axios";
import { Flag, X, AlertTriangle, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "harassment", label: "Harassment or threatening behavior" },
  { value: "fake_profile", label: "Fake profile / impersonation" },
  { value: "spam", label: "Spam or commercial solicitation" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "skill_misrep", label: "Significantly misrepresented skill level" },
  { value: "no_show", label: "Repeated no-shows or cancellations" },
  { value: "safety", label: "Real-world safety concern" },
  { value: "other", label: "Other" },
];

export default function ReportIssueModal({ reportedUser, onClose }) {
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!category) { setError("Please select a category"); return; }
    if (description.trim().length < 10) { setError("Please provide a brief description (at least 10 characters)"); return; }
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/reports`,
        { reported_user_id: reportedUser.id, category, description: description.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit report. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-modal-title"
      className="fixed inset-0 bg-brand-navy/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="report-issue-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button onClick={onClose} aria-label="Close report" className="absolute top-4 right-4 text-gray-400 hover:text-brand-navy transition-colors">
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-brand-green" />
            </div>
            <h2 className="text-xl font-heading font-bold text-brand-navy mb-1">Report submitted</h2>
            <p className="text-gray-500 text-sm mb-4">Our Trust & Safety team will review within 24 hours. Thank you for keeping the community safe.</p>
            <button onClick={onClose} className="px-6 py-2 bg-brand-navy text-white rounded-xl text-sm font-bold hover:bg-brand-navy/90 transition-colors dark-section">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Flag size={16} className="text-red-500" />
              </div>
              <div>
                <h2 id="report-modal-title" className="font-bold text-brand-navy">Report {reportedUser.name}</h2>
                <p className="text-xs text-gray-400">Reports are anonymous to the reported user.</p>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-sm text-red-600 mb-4">
                <AlertTriangle size={14} /> {error}
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="Issue-Category" className="block text-sm font-medium text-brand-navy mb-1.5">Issue category <span aria-hidden="true" className="text-brand-orange">*</span></label><select id="Issue-Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="report-category-select"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-brand-navy text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-brand-green/40"
              >
                <option value="">Select a category…</option>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            <div className="mb-5">
              <label htmlFor="Description" className="block text-sm font-medium text-brand-navy mb-1.5">Description <span aria-hidden="true" className="text-brand-orange">*</span></label><textarea id="Description"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe the issue..."
                data-testid="report-description-input"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-brand-navy placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{description.length}/500 characters</p>
            </div>

            <button
              onClick={submit}
              disabled={saving}
              data-testid="submit-report-btn"
              className="w-full py-2.5 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 shadow-sm"
            >
              {saving ? "Submitting…" : "Submit Report"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
